import React, { useState, useEffect } from 'react';
import { Modal } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import ModalEditBody from './ModalEditBody';
import ModalEditBodyDelete from './ModalEditBodyDelete';
import ModalEditFooter from '../../common/ModalEditFooter';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, createTradingCompanyInformation, updateTradingCompanyInformation, deleteTradingCompanyInformation } from '../../../redux/slices/manageTradingCompanyInformation';
import { EDIT_ACTION } from '../../../constants';
import type { InformationType, CreateInformationParams, UpdateInformationParamsClient, DeleteInformationParams } from '../../../types/information';

const ModalEditTradingCompanyInformation = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { dialogMode, isDialogOpen, information, informations } = useSelector((state: RootState) => state.manageTradingCompanyInformation);
  const [order, setOrder] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [buttonLabel, setButtonLabel] = useState<string>('');
  const [deleteFlag, setDeleteFlag] = useState<boolean>(false);
  const [isUniqueOrder, setIsUniqueOrder] = useState<boolean>(false);
  const isPositiveOrder: boolean = order > 0;

  // SIDE EFFECT
  // ================================================================================
  /**
   * 確認用
   */
  // useEffect(() => {
  //   console.log(isUniqueCode);
  // }, [isUniqueCode]);

  /**
   * 新規作成モード以外では初期値を入れておく
   */
  useEffect(() => {
    if (dialogMode !== EDIT_ACTION.CREATE && information) {
      setOrder(information.order);
      setMessage(information.message);
      setIsDraft(information.isDraft);
      setIsUniqueOrder(true);
    }
  }, [information]);

  /**
   * モードに併せてタイトルとボタン名を設定
   */
  useEffect(() => {
    if (dialogMode === EDIT_ACTION.CREATE) {
      setDialogTitle(t('information.alert.dialog.titleCreate'));
      setButtonLabel(t('dialog.create'));
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      setDialogTitle(t('information.alert.dialog.titleUpdate'));
      setButtonLabel(t('dialog.update'));
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setDialogTitle(t('information.alert.dialog.titleDelete'));
      setButtonLabel(t('dialog.delete'));
    } else {
      setDialogTitle('');
      setButtonLabel('');
    }
  }, [dialogMode]);

  /**
   * orderの重複チェック
   */
  useEffect(() => {
    // 新規作成モードでは同じorderのものがないかチェック
    if (dialogMode === EDIT_ACTION.CREATE) {
      const displayInformations: InformationType[] | undefined = informations?.filter((info) => info.isDraft === false);
      const sameOrder: InformationType | undefined = displayInformations?.find((info) => info.order === order);
      if (sameOrder !== undefined) {
        setIsUniqueOrder(false);
      } else {
        setIsUniqueOrder(true);
      }
      // 更新モードでは自分以外のorganizationBasesに同じ名前のものがないかチェック
    } else if (dialogMode === EDIT_ACTION.UPDATE) {
      const displayInformations: InformationType[] | undefined = informations?.filter((info) => info.isDraft === false);
      const sameOrder: InformationType | undefined = displayInformations?.find((info) => {
        if (info.id !== information.id && info.order === order) {
          return true;
        } else {
          return false;
        }
      });
      if (sameOrder !== undefined) {
        setIsUniqueOrder(false);
      } else {
        setIsUniqueOrder(true);
      }
      // 削除モードでは何もしない
    } else if (dialogMode === EDIT_ACTION.DELETE) {
      setIsUniqueOrder(false);
    } else {
      setIsUniqueOrder(false);
    }
  }, [order]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setOrder(0);
    setMessage('');
    setIsDraft(true);
    setDeleteFlag(false);
  };

  /**
   * タイトルを変更した時
   */
  const handleOrderChange = (event: React.ChangeEvent<{ value: number }>): void => {
    const newOrder: number = event.target.value;
    if (newOrder >= 0) {
      setOrder(Number(newOrder));
    } else {
      // do nothing
    }
  };

  /**
   * 本文を変更した時
   */
  const handleMessageChange = (event: React.ChangeEvent<{ value: string }>): void => {
    setMessage(event.target.value);
  };

  /**
   * 下書きフラグを変更した時
   */
  const handleToggleDraft = (): void => {
    setIsDraft(!isDraft);
    if (isDraft === true) {
      setOrder(order);
    }
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    // 新規作成時
    if (dialogMode === EDIT_ACTION.CREATE) {
      // setTradingCompanyCode(tradingCompanyCode); user情報から入手
      const params: CreateInformationParams = {
        order,
        message,
        isDraft,
      };
      dispatch(createTradingCompanyInformation(params));

      // 更新時
    } else if (dialogMode === EDIT_ACTION.UPDATE && information) {
      const params: UpdateInformationParamsClient = {
        id: information.id,
        order,
        message,
        isDraft,
      };
      dispatch(updateTradingCompanyInformation(params));

      // 削除時
    } else if (dialogMode === EDIT_ACTION.DELETE && information) {
      const params: DeleteInformationParams = {
        id: information.id,
      };
      dispatch(deleteTradingCompanyInformation(params));

      // その他
    } else {
      // do nothing
    }
    resetModal();
  };

  /**
   * モーダルでDeleteチェックをクリックした時
   */
  const handleToggleDelete = (): void => {
    setDeleteFlag(!deleteFlag);
  };

  // VIEW
  // ================================================================================
  return (
    <Modal id="modalEditTradingCompanyInformation" open={isDialogOpen} maxWidth={'xs'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'info'} title={dialogTitle} />
      {/* 新規作成時・更新時 */}
      {(dialogMode === EDIT_ACTION.CREATE || dialogMode === EDIT_ACTION.UPDATE) && (
        <ModalEditBody
          labelOrder={t('information.order')}
          labelMessage={t('information.message')}
          labelDraft={t('information.draft')}
          order={order}
          message={message}
          checked={isDraft}
          invalidOrder={!isDraft && (!isPositiveOrder || !isUniqueOrder)}
          invalidMessage={!message}
          onChangeOrder={handleOrderChange}
          onChangeMessage={handleMessageChange}
          onChangeDraft={handleToggleDraft}
        />
      )}
      {/* 削除時 */}
      {dialogMode === EDIT_ACTION.DELETE && <ModalEditBodyDelete description={t('information.alert.dialog.card2')} message={message} />}
      <ModalEditFooter
        mode={dialogMode}
        theme={dialogMode === EDIT_ACTION.DELETE ? 'danger' : 'primary'}
        disabled={
          (dialogMode === EDIT_ACTION.CREATE && (!message || (!isDraft && (!isUniqueOrder || !isPositiveOrder)))) ||
          (dialogMode === EDIT_ACTION.UPDATE && (!message || (!isDraft && (!isUniqueOrder || !isPositiveOrder)))) ||
          (dialogMode === EDIT_ACTION.DELETE && !deleteFlag)
        }
        labelCancel={t('dialog.cancel')}
        labelSave={buttonLabel}
        labelCheckBox={t('dialog.validDelete')}
        deleteFlag={deleteFlag}
        onCancel={handleCancel}
        onSave={handleSave}
        onToggle={handleToggleDelete}
      />
    </Modal>
  );
};

export default ModalEditTradingCompanyInformation;
